/**
 * Line-style (not block) anchored header menu used by the Activity attribute
 * control and the Value/Units header on /logs.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Check from '@mui/icons-material/Check';
import * as S from './ActivityLogsHeaderMenu.styled';

export type ActivityLogsHeaderMenuOption = {
  value: string,
  label: string,
  hint?: string,
  icon: React.ReactNode,
};

type ActivityLogsHeaderMenuProps = {
  /** Accessible name for the trigger. */
  ariaLabel: string,
  /** Tooltip when closed. */
  title?: string,
  /** Uppercase section label inside the popup. */
  sectionLabel?: string,
  value: string,
  options: ActivityLogsHeaderMenuOption[],
  onChange: (value: string) => void,
  /**
   * Trigger content: icon-only (Activity attribute) or current label (Value).
   * Defaults to the selected option's label.
   */
  trigger?: React.ReactNode,
  /** Prefer a compact line icon button (no bordered block). */
  lineTrigger?: boolean,
  /** Drop the left margin (Value header is the whole cell). */
  flush?: boolean,
};

const ActivityLogsHeaderMenu = ({
  ariaLabel,
  title,
  sectionLabel,
  value,
  options,
  onChange,
  trigger,
  lineTrigger = true,
  flush = false,
}: ActivityLogsHeaderMenuProps) => {
  const [open, setOpen] = useState(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value) || options[0];

  const updatePopupPos = () => {
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) return;
    const rect = anchor.getBoundingClientRect();
    const popupH = popup.offsetHeight;
    const popupW = popup.offsetWidth;
    const gap = 6;
    let top = rect.bottom + gap;
    if (top + popupH > window.innerHeight - 8) {
      top = Math.max(8, rect.top - popupH - gap);
    }
    // Prefer aligning to the right edge of sticky Value headers.
    let left = rect.right - popupW;
    left = Math.max(8, Math.min(left, window.innerWidth - popupW - 8));
    setPopupPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPopupPos(null);
      return undefined;
    }
    updatePopupPos();
    const onReposition = () => updatePopupPos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, options, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)
        && !popupRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <S.Anchor ref={anchorRef} $flush={flush}>
      <S.LineTrigger
        type="button"
        $open={open}
        $line={lineTrigger}
        aria-label={ariaLabel}
        aria-expanded={open}
        title={title || ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {trigger != null ? trigger : (
          <>
            <S.TriggerLabel>{selected?.label || value}</S.TriggerLabel>
            <S.TriggerCaret $open={open} />
          </>
        )}
      </S.LineTrigger>
      {open && (
        <S.Popup
          ref={popupRef}
          role="menu"
          style={popupPos
            ? { top: popupPos.top, left: popupPos.left }
            : { visibility: 'hidden' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {sectionLabel ? (
            <S.SectionLabel>{sectionLabel}</S.SectionLabel>
          ) : null}
          <S.OptionList>
            {options.map((opt) => {
              const checked = opt.value === value;
              return (
                <S.OptionRow
                  key={opt.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={checked}
                  $checked={checked}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <S.OptionIcon>{opt.icon}</S.OptionIcon>
                  <S.OptionText>
                    <S.OptionTitle>{opt.label}</S.OptionTitle>
                    {opt.hint ? <S.OptionHint>{opt.hint}</S.OptionHint> : null}
                  </S.OptionText>
                  <S.OptionCheck $checked={checked}>
                    {checked ? <Check fontSize="inherit" /> : null}
                  </S.OptionCheck>
                </S.OptionRow>
              );
            })}
          </S.OptionList>
        </S.Popup>
      )}
    </S.Anchor>
  );
};

export default ActivityLogsHeaderMenu;
